import type { AxiosError, Cancel } from 'axios'
import type { BaseSyntheticEvent, DragEvent, ReactElement } from 'react'
import { List, fromJS } from 'immutable'
import { formField } from '@epages/react-components'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { isCancel } from 'axios'
import { unionWith, uniq } from 'lodash-es'
import cc from 'classcat'

import useUploadHelper, { Status } from '../../../../../utils/hooks/useUploadHelper'
import { ImageUploadControl } from '../../../../UnsplashImageSearch/components/ImageUploadControl'
import { MAXIMUM_NUMBER_OF_IMAGES } from './ImageGallerySettings'
import { Popover } from '../../../../Popover'
import type { TCustomFile } from '../../../../../utils/hooks/useUploadHelper'
import { allowedMimeTypes, isValidMimeType } from '../../daliConfig'
import { useTranslate } from '../../../../../utils/translate'
import ImageEditPopover from './ImageEditPopover'
import ProgressRing from './ProgressRing'
import useHelpCenterLink from '../../../../../utils/hooks/useHelpCenterLink'
import usePrevious from '../../../../../utils/hooks/usePrevious'

const blankGif = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

type ErrorCodes =
  | 'requestEntityTooLarge'
  | 'unsupportedMediaType'
  | 'requestCanceled'
  | 'gifHasTooManyFrames'
  | 'imageResolutionTooBig'
  | 'unknown'
const statusToErrorCode = {
  413: 'requestEntityTooLarge',
  415: 'unsupportedMediaType',
} as const
const getErrorIndicator = (errorOrCancel?: AxiosError | Cancel): ErrorCodes => {
  if (!errorOrCancel) return 'unknown'
  if (isCancel(errorOrCancel)) return 'requestCanceled'

  const error = errorOrCancel as AxiosError<{ message: string }> // until https://github.com/axios/axios/pull/5595 is merged
  if (error.response?.data?.message?.startsWith('Animated image has too many frames.')) {
    return 'gifHasTooManyFrames'
  }
  if (error.response?.data?.message?.startsWith('Uploaded image resolution across all frames is too big.')) {
    return 'imageResolutionTooBig'
  }
  return (error.response && statusToErrorCode[error.response?.status]) || 'unknown'
}

type ErrorMessageProps = {
  errors: {
    fileName: string
    err?: AxiosError | Cancel
  }[]
  helpCenterUrl: string
} & TranslateProps

function ErrorMessages({ t, errors, helpCenterUrl }: Readonly<ErrorMessageProps>): ReactElement | null {
  const errorMessages: Record<string, string[]> = {}
  errors.forEach((error) => {
    const errorKey = getErrorIndicator(error.err)
    // We do not want to show canceled errors.
    if (errorKey === 'requestCanceled') return
    // Spreading on objects is failprove against falsy values, they will be ignored,
    // e.g. `{...false, a: 'b'}` is valid.
    // Array spread is not so nice to falsy values and will break, so we need a fallback.
    errorMessages[errorKey] = uniq([...(errorMessages[errorKey] || []), error.fileName])
  })

  if (Object.keys(errorMessages).length === 0) return null

  return (
    <scroll-shadow class="dali-plugin-imagegallery-error-shadow">
      <div className="dali-plugin-imagegallery-error" role="alert">
        {Object.keys(errorMessages).map((key) => (
          <div key={key}>
            <p>
              {t(`errorMessages.message`, { reason: t(`errorMessages.reason.${key}`) })}
              {key !== 'gifHasTooManyFrames' && key !== 'imageResolutionTooBig' && (
                <a
                  href={helpCenterUrl}
                  target="_blank"
                  className="ep-form-row-text-external-link"
                  rel="noreferrer noopener"
                >
                  {t('errorMessages.helpCenterLink')}
                </a>
              )}
            </p>
            <ul>
              {errorMessages[key].map((filename) => (
                <li key={filename}>{filename}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </scroll-shadow>
  )
}

type Props = Readonly<{
  data: ImmutableMap
  setFooterInfo: (status: string) => void
  setIsSaveDisabled: (isActive: boolean) => void
  onBusyStateChange: (isBusy: boolean) => void
  imageUrl: (src: string) => string
  previewUrl: (src: string) => string
  onChange: (data: ImmutableMap) => any
  storeFile: (file: File, config?: any) => Promise<Storage.Image>
  className?: string
}>

function getStoredImagesFromData(data: ImmutableMap) {
  const imagesInData: TCustomFile[] = data.get('images', List()).toJS()
  return imagesInData.filter((item) => typeof item.id === 'undefined')
}

function isSameImageUploadFile(a, b) {
  return a.id || b.id ? a.id === b.id : false
}

const ImageFieldMultipleRaw = forwardRef<HTMLDivElement, Props>((props, _ref) => {
  const {
    data,
    setFooterInfo,
    setIsSaveDisabled,
    onBusyStateChange,
    onChange,
    storeFile,
    imageUrl,
    previewUrl,
    className,
  } = props

  const popoverRefs = useRef(new Map())
  const helpCenterUrl = useHelpCenterLink('IMAGE_GALLERY')

  const [files, addFiles, isProcessing, messageCodes, deleteFile, editFile] = useUploadHelper(storeFile)
  const storedImages = getStoredImagesFromData(data)
  const allImages = storedImages
    .concat(files.pending, files.uploading, files.completed)
    .sort((a, b) => a.position - b.position)
  const previousCompletedFiles = usePrevious(files.completed)

  const [selectedImagePosition, setSelectedImagePosition] = useState<number | null>(null)

  const hasActiveRequest = files.pending.length > 0 || files.uploading.length > 0

  const fileInput = useRef<HTMLInputElement>(null)

  const t = useTranslate('interface', 'components.imageGalleryComponent')

  useEffect(() => {
    onBusyStateChange(hasActiveRequest)

    if (files.completed.length !== previousCompletedFiles?.length && !hasActiveRequest) {
      onChange(fromJS({ images: unionWith(storedImages, files.completed, isSameImageUploadFile) }))
    }
  }, [files, hasActiveRequest, storedImages, previousCompletedFiles, onChange, onBusyStateChange])

  useEffect(() => {
    const imageCount = allImages.length || 0
    setIsSaveDisabled(hasActiveRequest || imageCount > MAXIMUM_NUMBER_OF_IMAGES)
  }, [hasActiveRequest, allImages.length, setIsSaveDisabled])

  const getFilesFromLocal = (event: DragEvent & BaseSyntheticEvent) => {
    event.preventDefault()

    const filesFromLocal = Array.from(event.dataTransfer?.files || event.target.files)
    addFiles(filesFromLocal)
  }

  const selectImageToEdit = (position: number) => {
    setSelectedImagePosition(position)
  }

  const handleEditSave = (updatedImage: ImmutableMap) => {
    const updatedData = data.update('images', (images) =>
      images.map((image) => (image.get('position') === selectedImagePosition ? updatedImage : image)),
    )

    editFile(updatedImage)
    onChange(updatedData)
    setSelectedImagePosition(null)
  }

  const handleEditCancel = () => {
    setSelectedImagePosition(null)
  }

  const deleteImage = (file: TCustomFile) => {
    const updatedImages = data.get('images').filter((image) => image.get('position') !== file.position)
    onChange(data.set('images', updatedImages))

    deleteFile(file.id)

    setFooterInfo('delete')
  }

  const onDragOver = (event: DragEvent) => {
    const file = event.dataTransfer?.items?.[0]
    const isFirefox = event.dataTransfer?.items === undefined
    const isPlugin = window.dali.globalDragData

    // Files from outside of the browser are always allowed to be dropped and the browser will open them.
    // To prevent this behavior we prevent the default in all cases and do not allow unsupported files with
    // `dropEffect = 'none'`.
    // For firefox we need to set the dropEffect to copy in order to be able to drop at all.
    // Meaning we cannot check the file type on dragover in FF but we know if s/o's trying to drop one of our plugins.
    event.preventDefault()
    event.dataTransfer.dropEffect = (isFirefox && !isPlugin) || (file && !isValidMimeType(file)) ? 'copy' : 'none'
  }

  const handleImageError = (image) => {
    if (!image.target.src.includes('&reload=1')) image.target.src = `${image.target.src}&reload=1`
  }

  const renderEmpty = () => {
    return (
      <>
        <div
          className={cc(['dali-plugin-image-default', className])}
          onDragOver={onDragOver}
          onDrop={getFilesFromLocal}
        >
          <span className="dali-plugin-image-placeholder" />
          <div className="dali-plugin-image-button-wrapper">
            <div className="dali-plugin-image-info">{t('explanation')}</div>
            <ImageUploadControl
              onUnsplashSelect={(file) => addFiles([file])}
              onUploadClick={() => fileInput.current?.click()}
              buttonContent={<span>{t(`addImagesButton.label`)}</span>}
              buttonClassName="dali-button dali-plugin-image-button-upload"
            />
          </div>
        </div>
        <ErrorMessages t={t} errors={messageCodes} helpCenterUrl={helpCenterUrl} />
      </>
    )
  }

  const renderSpinner = () => (
    <div className="dali-settingslayer-imagegallery-spinner dali-plugin-image-busy-indicator">
      <span className="dali-plugin-image-spinner" />
    </div>
  )

  const previewItem = (file: TCustomFile) => {
    const src = file.localPreview ? file.localPreview : file.src ? previewUrl(imageUrl(file.src)) : blankGif

    return (
      <div key={file.position}>
        <div
          className="dali-plugin-image-preview dali-grid-element-editview"
          ref={(node) => {
            if (node) {
              popoverRefs.current.set(file.position, node)
            } else {
              popoverRefs.current.delete(file.position)
            }
          }}
        >
          <img src={src} alt={file.alt} onError={handleImageError} />
          <div className="dali-plugin-image-button-wrapper">
            <button
              type="button"
              className="dali-grid-element-edit-button dali-grid-element-edit-button-edit"
              title={t(':components.pluginContextBarComponent.editElementButton.label')}
              onClick={() => selectImageToEdit(file.position)}
            />
            <button
              className="dali-image-button-delete"
              title={t('deleteImageButton.tooltip')}
              onClick={() => deleteImage(file)}
              type="button"
            />
          </div>
          {(file.status === Status.PENDING || file.status === Status.UPLOADING) && (
            <div className="dali-plugin-imagegallery-loading-indicator">
              <ProgressRing percentage={file.percentage} />
              <button onClick={() => file.cancelToken.cancel()} type="button">
                {t('cancelUpload.label')}
              </button>
            </div>
          )}
          <Popover
            open={selectedImagePosition === file.position}
            onOpenChange={(isOpen) => {
              if (!isOpen) handleEditCancel()
            }}
            className="dali-plugin-imagegallery-popover"
            side="left"
            align="start"
            sideOffset={-5}
            virtualRef={{ current: popoverRefs.current.get(file.position) }}
            avoidCollisions
            usePortal
          >
            {selectedImagePosition && (
              <ImageEditPopover
                data={data}
                selectedImagePosition={selectedImagePosition}
                storeFile={storeFile}
                imageUrl={imageUrl}
                previewUrl={previewUrl}
                onApply={handleEditSave}
                onCancel={handleEditCancel}
                helpCenterUrl={helpCenterUrl}
              />
            )}
          </Popover>
        </div>
      </div>
    )
  }

  const renderPreview = () => (
    <>
      <scroll-shadow>
        <div className="dali-plugin-imagegallery-grid">{allImages.map(previewItem)}</div>
      </scroll-shadow>

      <div className="dali-plugin-imagegallery-content-footer">
        <ImageUploadControl
          onUnsplashSelect={(file) => addFiles([file])}
          onUploadClick={() => fileInput.current?.click()}
          buttonContent={<span>{t(`addImagesButton.label`)}</span>}
          buttonClassName="dali-plugin-imagegallery-button-upload"
        />
      </div>

      <ErrorMessages t={t} errors={messageCodes} helpCenterUrl={helpCenterUrl} />
    </>
  )

  return (
    <>
      {isProcessing ? renderSpinner() : allImages.length ? renderPreview() : renderEmpty()}

      <input
        ref={fileInput}
        type="file"
        multiple
        accept={allowedMimeTypes.join(',')}
        onChange={getFilesFromLocal}
        value=""
        data-testid="uploadField"
      />
    </>
  )
})

ImageFieldMultipleRaw.displayName = 'ImageFieldMultipleRaw'

export default formField()(ImageFieldMultipleRaw)
